import { z } from 'zod';

/**
 * Runtime configuration, fetched from /config.json at boot.
 *
 * Not `import.meta.env`, deliberately. A `VITE_`-prefixed variable is compiled into the bundle,
 * which means a different bundle per environment — and the whole point of the image contract is
 * that one artifact promotes from dev to production unchanged, with the platform mounting a
 * different config.json over it.
 *
 * Parsed through Zod like every other boundary: a malformed config.json should fail loudly at
 * boot with a message naming the problem, not surface as `undefined is not a function` when
 * someone clicks sign in.
 */
const ConfigSchema = z.object({
  auth: z.object({
    url: z.string().url(),
    realm: z.string().min(1),
    clientId: z.string().min(1),
  }),
  // Empty string means same-origin, which is the normal case: Vite proxies /api in development
  // and the API serves the SPA from wwwroot in production, so the browser sees one origin either
  // way.
  apiBaseUrl: z.string(),
  hubUrl: z
    .string()
    .url()
    .refine((value) => {
      try {
        const url = new URL(value);
        return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password;
      } catch {
        return false;
      }
    }, 'must be an HTTP(S) URL without credentials')
    .default('https://hub.hazcheck.com'),
});

export type AppConfig = z.infer<typeof ConfigSchema>;

/**
 * The API client id, which is also the token audience and the key under `resource_access` that
 * roles are read from. Hardcoded rather than configured because it must match the API's
 * `AUTH_AUDIENCE` exactly — making it configurable would let the two drift, and the failure mode
 * is "signed in but no roles", which looks like a permissions bug rather than a config one.
 */
export const API_CLIENT_ID = 'campaigns-api';

let cached: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (cached !== null) return cached;

  const response = await fetch('/config.json', { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(
      `Could not fetch /config.json (${response.status}). In development it lives in src/Web/public/.`,
    );
  }

  const parsed = ConfigSchema.safeParse(await response.json());

  if (!parsed.success) {
    throw new Error(
      `/config.json is malformed: ${parsed.error.issues.map((i) => `${i.path.join('.')} ${i.message}`).join('; ')}`,
    );
  }

  cached = parsed.data;
  return cached;
}

/** Synchronous access after boot. Throws rather than returning a partial config. */
export function getConfig(): AppConfig {
  if (cached === null) {
    throw new Error('getConfig() was called before loadConfig() resolved.');
  }

  return cached;
}
